import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { useQueryClient } from "react-query";
import { useAudienceListView } from "../AudienceList.context";
import { useGetRetentionIntegrationQuery } from "@tiny/api";
import { SortOption } from "./AudienceFilter.types";

export function useAudienceFilter() {
  const {
    audiences,
    enableBulkDelete,
    deleteDisabled,
    enableSelectionActions,
    isItemSelected,
    nrOfSelectedAudiences,
    onDeleteAudiences,
    onClickMergeAudiences,
    reloadAudiences,
    searchKeyword,
    selectItem,
    selectionDisabled,
    setSearchKeyword,
    setSortOption,
    sortOption,
    sortOptions,
    unselectAll,
    unselectItem,
    mergeDisabled,
    handleOpenAudienceSummary,
    onClickTrafficControl,
    handleOpenSegmentPerformance,
  } = useAudienceListView();

  const { navigate } = useTinyHost();
  const queryClient = useQueryClient();

  const { data: retentionIntegration } = useGetRetentionIntegrationQuery();
  const isRetentionActive = retentionIntegration?.accountIntegrated === true;

  const [retentionDialogOpen, setRetentionDialogOpen] = React.useState(false);
  const [manageRetentionDialogOpen, setManageRetentionDialogOpen] =
    React.useState(false);

  const openRetentionDialog = React.useCallback(
    () => setRetentionDialogOpen(true),
    []
  );
  const closeRetentionDialog = React.useCallback(
    () => setRetentionDialogOpen(false),
    []
  );
  const closeManageRetentionDialog = React.useCallback(
    () => setManageRetentionDialogOpen(false),
    []
  );

  const handleRetentionSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(["GET", "/product/retention/integration"]);
    setRetentionDialogOpen(false);
    reloadAudiences();
    setManageRetentionDialogOpen(true);
  }, [queryClient, reloadAudiences]);

  const handleManageRetention = React.useCallback(() => {
    navigate.to("/main/plans-and-billing/manage-credits");
  }, [navigate]);

  const onChangeSort = React.useCallback(
    (e: any, item: SortOption) => {
      setSortOption(item);
    },
    [setSortOption]
  );

  const selectedText = React.useMemo(
    () => `${nrOfSelectedAudiences} selected`,
    [nrOfSelectedAudiences]
  );

  const hasSelected = React.useMemo(
    () => nrOfSelectedAudiences > 0,
    [nrOfSelectedAudiences]
  );

  const availablesForSelect = React.useCallback(() => {
    return audiences?.content || [];
  }, [audiences?.content]);

  const areAllSelectedOnCurrentPage = React.useMemo(() => {
    const availableForSelect = availablesForSelect();
    return (
      availableForSelect.length > 0 &&
      availableForSelect.filter(isItemSelected).length ===
        availableForSelect.length
    );
  }, [availablesForSelect, isItemSelected]);

  const toggleAllOnCurrentPage = React.useCallback(() => {
    const availableForSelect = availablesForSelect();
    availableForSelect?.forEach(
      areAllSelectedOnCurrentPage
        ? unselectItem
        : (item) => {
            !isItemSelected(item) && selectItem(item);
          }
    );
  }, [
    areAllSelectedOnCurrentPage,
    availablesForSelect,
    selectItem,
    unselectItem,
    isItemSelected,
  ]);

  const checkAllDisabled = React.useMemo(() => {
    const availables = availablesForSelect();
    return availables.length === 0 || selectionDisabled;
  }, [availablesForSelect, selectionDisabled]);

  const showMerge = +nrOfSelectedAudiences > 1;

  return {
    areAllSelectedOnCurrentPage,
    checkAllDisabled,
    enableBulkDelete,
    enableSelectionActions,
    hasSelected,
    nrOfSelectedAudiences,
    onChangeSort,
    onClickMergeAudiences,
    onDeleteAudiences,
    reloadAudiences,
    searchKeyword,
    selectedText,
    selectionDisabled,
    setSearchKeyword,
    sortOptions,
    sortedBy: sortOption.value,
    toggleAllOnCurrentPage,
    unselectAll,
    showMerge,
    deleteDisabled,
    mergeDisabled,
    handleOpenAudienceSummary,
    onClickTrafficControl,
    handleOpenSegmentPerformance,
    isRetentionActive,
    retentionDialogOpen,
    openRetentionDialog,
    closeRetentionDialog,
    handleRetentionSuccess,
    handleManageRetention,
    manageRetentionDialogOpen,
    closeManageRetentionDialog,
  };
}
